import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner';
import { useWorkerStatus } from '../../hooks/useWorkerStatus';
import { useLanguage } from '../../i18n/LanguageContext';
import operatorService, { WorkerAssignment, ProductionSession } from '../../services/operatorService';
import { saveMobileState, saveClockInProgress, loadClockInProgress, clearClockInProgress } from '../../utils/mobileSessionStorage';
import { normalizeBadgeCode, isBadgeCode } from '../../utils/barcodeUtils';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useTour } from '../../components/TourProvider';
import TourProductionDemo, { TourProductionStep } from '../../components/TourProductionDemo';
import type { EmployeeInfo, SessionInfo } from '../../types';
import SupervisorOverrideModal from '../Operator/modals/SupervisorOverrideModal';
import toast from 'react-hot-toast';
import {
  QrCode,
  User,
  Loader2,
  CheckCircle,
  RefreshCw,
  Menu,
  X,
  Camera,
  LayoutGrid,
} from 'lucide-react';
import CameraScannerModal from '../../components/CameraScannerModal';
import MobileClockInSidebar from './components/MobileClockInSidebar';
import ScanBadgeStep from './components/ScanBadgeStep';

interface StationInfo {
  station_id: number;
  station_name: string;
  station_barcode: string;
  status: string;
  printer_mac_id?: string; // Config key for printer MAC (e.g. "printer_test")
}

type Step = 'scan_station' | 'scan_badge' | 'auto_packer' | 'supervisor_choice' | 'confirm_relief' | 'success';

// Variant prop to control theme and navigation
export interface MobileClockInProps {
  variant?: 'operator' | 'supervisor';
  embedded?: boolean; // When true, hides mobile header/sidebar and uses Layout's page title
}


const MobileClockIn: React.FC<MobileClockInProps> = ({ variant = 'operator', embedded = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Theme colors based on variant
  const isSupervisor = variant === 'supervisor';
  const headerBg = 'bg-blue-600 dark:bg-blue-700';
  const accentColor = 'text-blue-600';
  const accentBg = 'bg-blue-600';
  const homeRoute = isSupervisor ? '/supervisor' : '/operator/clock-in';

  // Sidebar state (only used when not embedded)
  const [showSideMenu, setShowSideMenu] = useState(false);

  // State
  const [step, setStep] = useState<Step>('scan_station');
  const [station, setStation] = useState<StationInfo | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [employee, setEmployee] = useState<EmployeeInfo | null>(null);
  const [assignments, setAssignments] = useState<{ operator: WorkerAssignment | null; packer: WorkerAssignment | null }>({
    operator: null,
    packer: null,
  });
  // Real-time worker status from useWorkerStatus hook — overrides local assignments for display
  const workerStatus = useWorkerStatus(station?.station_id, session?.sub_session_number, !!station && !!session);
  // Merge: use hook data when available (has real-time break status), fall back to local assignments
  const displayAssignments = {
    operator: workerStatus.operator ?? assignments.operator,
    packer: workerStatus.packer ?? assignments.packer,
  };
  const isCurrentSessionOperator = useCallback((employeeNumber: string) => {
    return displayAssignments.operator?.employee_number === employeeNumber;
  }, [displayAssignments.operator]);
  const hasRoleConflict = useCallback((role: 'operator' | 'packer', employeeNumber: string) => {
    if (role === 'packer') {
      return displayAssignments.operator?.employee_number === employeeNumber;
    }
    return displayAssignments.packer?.employee_number === employeeNumber;
  }, [displayAssignments.operator, displayAssignments.packer]);
  const [selectedRole, setSelectedRole] = useState<'operator' | 'packer' | null>(null);
  const [roleSelectionSource, setRoleSelectionSource] = useState<'auto_packer' | 'supervisor_choice'>('auto_packer');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Supervisor override modal state
  const [showSupervisorModal, setShowSupervisorModal] = useState(false);
  const [supervisorInfo, setSupervisorInfo] = useState<{ id: string; name: string; employee_number: string } | null>(null);
  const [fullSessionData, setFullSessionData] = useState<ProductionSession | null>(null);

  // Manual entry state
  const [manualInput, setManualInput] = useState('');
  // Camera scanner state
  const [showCamera, setShowCamera] = useState(false);

  // Refs for input focus (Android doesn't respect autoFocus)
  const stationInputRef = useRef<HTMLInputElement>(null);
  const badgeInputRef = useRef<HTMLInputElement>(null);

  // Tour mode: reactively inject mock data based on current tour step
  const { isTourMode, tourEvent } = useTour();
  const [tourProductionStep, setTourProductionStep] = useState<TourProductionStep | null>(null);

  useEffect(() => {
    if (!isTourMode) {
      // Only reset production demo when tour fully stops (not during step transitions)
      // Check tourEvent to distinguish "tour ended" from "paused between steps"
      if (!tourEvent) {
        setTourProductionStep(null);
      }
      return;
    }

    // Production demo steps — show mock production UI
    if (tourEvent === 'production-scanning') {
      setTourProductionStep('scanning');
      return;
    } else if (tourEvent === 'production-badge-scan') {
      setTourProductionStep('badge-scan');
      return;
    } else if (tourEvent === 'production-finish') {
      setTourProductionStep('finish');
      return;
    } else if (tourEvent === 'production-waste') {
      setTourProductionStep('waste');
      return;
    }

    // Clock-in flow steps — show normal clock-in UI
    setTourProductionStep(null);

    if (tourEvent === 'scan-station') {
      setStep('scan_station');
      setStation(null);
      setSession(null);
      setEmployee(null);
      setError(null);
    } else if (tourEvent === 'scan-badge') {
      setStation({ station_id: 1, station_name: 'Station 1', station_barcode: '#STN-001', status: 'active', printer_mac_id: '' });
      setSession({
        sub_session_number: 'DEMO-001',
        session_number: 'DEMO-001',
        product_name: 'Demo Product',
        product_sku: 'DEMO-SKU',
        status: 'active',
        lot_number: 'LOT-001',
      } as SessionInfo);
      setAssignments({ operator: null, packer: null });
      setStep('scan_badge');
      setError(null);
    } else if (tourEvent === 'scan-success') {
      setEmployee({ employee_number: '1234', display_name: 'Demo Worker' } as EmployeeInfo);
      setSelectedRole('packer');
      setStep('success');
      setError(null);
    } else if (!tourEvent) {
      // Non-clockin tour step — reset to default
      setStep('scan_station');
      setStation(null);
      setSession(null);
      setEmployee(null);
      setError(null);
    }
  }, [isTourMode, tourEvent]);

  // Auto-focus inputs when step changes (works on Android)
  useEffect(() => {
    if (step === 'scan_station' && stationInputRef.current) {
      setTimeout(() => stationInputRef.current?.focus(), 100);
    }
  }, [step]);

  useEffect(() => {
    if (step === 'scan_badge' && badgeInputRef.current) {
      setTimeout(() => badgeInputRef.current?.focus(), 100);
    }
  }, [step]);

  // Restore clock-in progress from sessionStorage on mount (survives browser restart)
  useEffect(() => {
    if (isTourMode) return; // Don't restore saved progress during tour
    const saved = loadClockInProgress();
    if (saved?.station && saved?.session) {
      setStation(saved.station as StationInfo);
      setSession(saved.session as SessionInfo);
      setAssignments(saved.assignments as { operator: WorkerAssignment | null; packer: WorkerAssignment | null });
      setStep('scan_badge');
    }
  }, []);

  // Auto-packer: when step transitions to 'auto_packer', automatically clock in as packer
  useEffect(() => {
    if (step !== 'auto_packer' || !station || !employee || !session) return;

    if (isCurrentSessionOperator(employee.employee_number)) {
      setError('A different worker must clock in as packer for this session.');
      setStep('scan_badge');
      return;
    }

    const currentPacker = assignments.packer;
    if (currentPacker && currentPacker.employee_number !== employee.employee_number) {
      // Packer slot is occupied by someone else — confirm relief
      setRoleSelectionSource('auto_packer');
      setStep('confirm_relief');
    } else {
      // Packer slot is free — clock in directly
      performClockIn('packer', false);
    }
  }, [assignments.packer, employee, isCurrentSessionOperator, session, station, step]);

  // Handle barcode scan
  const handleScan = useCallback(async (barcode: string) => {
    setError(null);
    setIsLoading(true);

    try {
      if (barcode.startsWith('#STN-') || (step === 'scan_station' && !isBadgeCode(barcode))) {
        // Station barcode
        const code = barcode.startsWith('#STN-') ? barcode : `#STN-${barcode}`;
        const result = await operatorService.scanStation(code);

        if (!result.success) {
          setError(result.message || 'Station not found');
          setIsLoading(false);
          return;
        }

        setStation(result.station!);

        if (!result.session) {
          const errorCode = result.errorCode;
          const subSessionNumber = result.sub_session_number || '';
          let errorMsg: string;
          switch (errorCode) {
            case 'materials_not_received':
              errorMsg = t('mobile.materialsNotReceived').replace('{subSessionNumber}', subSessionNumber);
              break;
            case 'awaiting_assignment':
              errorMsg = t('mobile.awaitingAssignment').replace('{subSessionNumber}', subSessionNumber);
              break;
            case 'scheduled_future':
              errorMsg = t('mobile.scheduledFuture').replace('{subSessionNumber}', subSessionNumber);
              break;
            case 'session_completed':
              errorMsg = t('mobile.sessionCompleted');
              break;
            case 'session_cancelled':
              errorMsg = t('mobile.sessionCancelled');
              break;
            default:
              errorMsg = result.message || t('mobile.noActiveSession');
          }
          setError(errorMsg);
          setIsLoading(false);
          return;
        }

        setSession(result.session);
        setAssignments(result.assignments || { operator: null, packer: null });
        setStep('scan_badge');
        // Persist station context so it survives browser restart before badge scan
        saveClockInProgress({
          station: result.station!,
          session: result.session,
          assignments: result.assignments || { operator: null, packer: null },
        });
        toast.success(result.station!.station_name);

      } else if (isBadgeCode(barcode) || step === 'scan_badge') {
        // Employee badge (handles @+, @i, @ prefixes)
        if (!station || !session) {
          setError('Please scan station first');
          setIsLoading(false);
          return;
        }

        const code = normalizeBadgeCode(barcode);
        const result = await operatorService.lookupEmployee(code);

        if (!result.success) {
          setError(result.message || 'Employee not found');
          setIsLoading(false);
          return;
        }

        setEmployee(result.employee!);

        // Check if employee is already assigned at the SAME sub-session — skip clock-in
        if (result.existing_assignment && result.existing_assignment.sub_session_number === session.sub_session_number) {
          const role = result.existing_assignment.role || 'packer';
          toast.success(t('clockInModal.alreadyHere') || 'Already clocked in at this session');
          clearClockInProgress();
          saveMobileState({
            session: session as Record<string, any>,
            station: station as Record<string, any>,
            employee: result.employee as Record<string, any>,
            boxCount: session.total_boxes || 0,
            role,
          });
          if (role === 'operator') {
            navigate('/operator/mobile-operator', { state: { station, session, employee: result.employee, employeeBoxCount: 0 } });
          } else {
            navigate('/operator/mobile-packer', { state: { station, session, employee: result.employee, employeeBoxCount: 0 } });
          }
          setIsLoading(false);
          return;
        }

        // Check if this is a supervisor - show choice screen (override vs clock-in)
        if (result.employee?.is_supervisor) {
          setSupervisorInfo({
            id: result.employee.user_id || result.employee.employee_number,
            name: result.employee.display_name,
            employee_number: result.employee.employee_number
          });
          setStep('supervisor_choice');
          toast.success(result.employee!.display_name);
        } else {
          if (isCurrentSessionOperator(result.employee!.employee_number)) {
            setError('A different worker must clock in as packer for this session.');
            setIsLoading(false);
            return;
          }

          // Regular employee - auto-select packer (operators scan in from Daily Work Queue)
          setSelectedRole('packer');
          setStep('auto_packer');
          toast.success(result.employee!.display_name);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Scan failed');
    } finally {
      setIsLoading(false);
    }
  }, [isCurrentSessionOperator, step, station, session, t]);

  // Barcode scanner hook
  useBarcodeScanner({
    onScan: handleScan,
    enabled: step === 'scan_station' || step === 'scan_badge',
    minLength: 4,
  });

  // Handle manual submit
  const handleManualSubmit = () => {
    if (manualInput.trim()) {
      handleScan(manualInput.trim());
      setManualInput('');
    }
  };

  // Handle supervisor override selection
  const handleSupervisorOverride = async () => {
    if (!supervisorInfo) return;
    if (!session) {
      setError(t('mobile.supervisorNoSession') || 'No active session found');
      return;
    }

    setIsLoading(true);
    // Try to fetch full session data via assigned operator, but fall back to station scan data
    const operatorId = assignments.operator?.employee_number || assignments.packer?.employee_number;
    if (operatorId) {
      try {
        const sessionData = await operatorService.getMySession(operatorId);
        if (sessionData.session) {
          setFullSessionData(sessionData.session);
          setShowSupervisorModal(true);
        } else {
          setError(t('mobile.supervisorNoSession') || 'No active session found');
        }
      } catch (err) {
        console.error('Failed to fetch session data:', err);
        setError(t('mobile.supervisorSessionError') || 'Failed to load session data');
      }
    } else {
      // No operator assigned yet - use session data from station scan
      // Convert SessionInfo to ProductionSession format for the modal
      const sessionForModal = {
        sub_session_number: session.sub_session_number,
        is_sub_session: true,
        session_number: session.session_number || session.sub_session_number,
        product_sku: session.product_sku || '',
        product_name: session.product_name || '',
        product_chinese: session.product_chinese || '',
        box_target_weight: session.product_unit_weight || 0,
        packing: parseInt(session.packing || '0') || 0,
        yield_factor: session.raw_material_yield || 0,
        station_id: station?.station_id || 0,
        station_number: station?.station_id || 0,
        station_name: station?.station_name || '',
        raw_material_item_number: session.raw_material_item_number || '',
        raw_material_name: session.raw_material_english || '',
        raw_material_chinese: session.raw_material_chinese || '',
        raw_material_lot: session.lot_number || '',
        supervisor_name: supervisorInfo.name,
        session_status: session.status,
        status: session.status,
        initial_material_weight: session.initial_material_weight || 0,
        target_output_weight: session.target_output_weight || 0,
        actual_output_weight: session.actual_output_weight || 0,
        waste_weight: 0,
        yield_percentage: 0,
        expected_yield: session.raw_material_yield || 0,
        started_at: session.started_at || null,
        paused_duration_seconds: session.paused_duration_seconds || 0,
      } as ProductionSession;

      setFullSessionData(sessionForModal);
      setShowSupervisorModal(true);
    }
    setIsLoading(false);
  };

  // Handle role selection
  const handleRoleSelect = async (role: 'operator' | 'packer', source: 'auto_packer' | 'supervisor_choice' = 'auto_packer') => {
    if (!station || !employee || !session) return;

    setSelectedRole(role);
    setRoleSelectionSource(source);

    if (hasRoleConflict(role, employee.employee_number)) {
      setError(`A different worker must clock in as ${role} for this session.`);
      return;
    }

    // Check if role is occupied by someone else
    const currentWorker = assignments[role];
    if (currentWorker && currentWorker.employee_number !== employee.employee_number) {
      setStep('confirm_relief');
      return;
    }

    await performClockIn(role, false);
  };

  // State for employee's box count in this shift
  const [employeeBoxCount, setEmployeeBoxCount] = useState(0);

  // Perform clock-in
  const performClockIn = async (role: 'operator' | 'packer', confirmRelief: boolean) => {
    if (!station || !employee) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await operatorService.clockIn({
        station_id: station.station_id,
        badge_code: `@+${employee.employee_number}`,
        role,
        confirm_relief: confirmRelief,
        auto_transfer: true, // Auto-transfer from other stations
      });

      if (!result.success) {
        if (result.action === 'needs_confirmation') {
          setStep('confirm_relief');
          return;
        }
        setError(result.message || 'Clock-in failed');
        return;
      }

      // Handle already_assigned case (resumed from break or truly already assigned)
      if (result.action === 'already_assigned') {
        toast.success('Resumed from break');
        navigateToRoleView(result.current_role || role, result.employee_box_count || 0, result.session);
        return;
      }

      // Capture employee's box count for this shift
      const boxCount = result.employee_box_count || 0;
      setEmployeeBoxCount(boxCount);

      // Update session with fresh data from clock-in response (includes timing info)
      if (result.session) {
        setSession(result.session);
      }

      // Success - navigate to role-specific view
      setStep('success');
      toast.success(t('clockIn.clockedIn'));

      setTimeout(() => {
        navigateToRoleView(role, boxCount, result.session);
      }, 1000);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Clock-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Navigate to role-specific view
  const navigateToRoleView = (role: string, empBoxCount: number = 0, freshSession?: any) => {
    // Use fresh session from clock-in response if available, otherwise use current session state
    const sessionToUse = freshSession || session;

    // Clock-in complete — clear the in-progress state and persist final state
    clearClockInProgress();
    saveMobileState({
      session: sessionToUse as Record<string, any>,
      station: station as Record<string, any>,
      employee: employee as Record<string, any>,
      boxCount: sessionToUse?.total_boxes || 0,
      role,
    });

    if (role === 'operator') {
      navigate('/operator/mobile-operator', { state: { station, session: sessionToUse, employee, employeeBoxCount: empBoxCount } });
    } else {
      navigate('/operator/mobile-packer', { state: { station, session: sessionToUse, employee, employeeBoxCount: empBoxCount } });
    }
  };

  // Handle relief confirmation
  const handleConfirmRelief = () => {
    if (selectedRole) {
      performClockIn(selectedRole, true);
    }
  };

  // Reset to start
  const handleReset = () => {
    setStep('scan_station');
    setStation(null);
    setSession(null);
    setEmployee(null);
    setSelectedRole(null);
    setAssignments({ operator: null, packer: null });
    setError(null);
    setManualInput('');
    clearClockInProgress();
  };

  // Get title based on step
  const getTitle = () => {
    switch (step) {
      case 'scan_station': return t('mobile.scanStation') || 'Scan Station';
      case 'scan_badge': return t('mobile.scanBadge') || 'Scan Badge';
      case 'auto_packer': return t('clockIn.packer') || 'Packer';
      case 'confirm_relief': return t('mobile.confirmRelief') || 'Confirm Relief';
      case 'success': return t('mobile.success') || 'Success';
    }
  };

  // When embedded in Layout, set the dynamic page title in the Layout header
  const dynamicTitle = embedded
    ? tourProductionStep
      ? 'Production — Demo'
      : `${getTitle()}${station?.station_name ? ` - ${station.station_name}` : ''}`
    : '';
  usePageTitle(dynamicTitle);

  // Tour production demo — show mock production UI instead of clock-in
  if (tourProductionStep) {
    return (
      <div className={`${embedded ? 'min-h-[calc(100vh-5rem)]' : 'min-h-screen bg-gray-100 dark:bg-gray-900'} flex flex-col`}>
        <TourProductionDemo activeStep={tourProductionStep} />
      </div>
    );
  }

  return (
    <div className={`${embedded ? 'min-h-[calc(100vh-5rem)]' : 'min-h-screen bg-gray-100 dark:bg-gray-900'} flex flex-col`}>
      {/* Header with Back Button (supervisor) or Hamburger Menu (operator) — hidden when embedded in Layout */}
      {!embedded && (
        <header className={`${headerBg} text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-2`}>
          {isSupervisor ? (
            <button
              onClick={() => navigate('/supervisor')}
              className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors flex-shrink-0"
            >
              <LayoutGrid className="w-6 h-6" />
            </button>
          ) : (
            <button
              onClick={() => setShowSideMenu(true)}
              className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors flex-shrink-0"
            >
              <Menu className="w-6 h-6" />
            </button>
          )}

          {/* Title and station name */}
          <div className="flex-1">
            <h1 className="text-lg font-bold leading-tight">{getTitle()}</h1>
            {station?.station_name && <p className="text-xs opacity-80">{station.station_name}</p>}
          </div>

          {/* Badge input - compact, pushed to the right next to X button */}
          {step === 'scan_badge' && (
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-1.5 bg-amber-500/30 rounded-lg px-2 py-2 w-[120px] animate-pulse ring-2 ring-amber-400/50">
                <User className="w-4 h-4 text-amber-300 flex-shrink-0" />
                <input
                  ref={badgeInputRef}
                  type="text"
                  inputMode="none"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
                  placeholder="Badge"
                  className="flex-1 bg-transparent text-white placeholder-white/70 outline-none text-sm min-w-0"
                  autoComplete="off"
                />
                {isLoading && <Loader2 className="w-4 h-4 animate-spin text-white/70 flex-shrink-0" />}
              </div>
              <button
                onClick={() => setShowCamera(true)}
                className="p-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
                title={t('cameraScanner.openCamera') || 'Use Camera'}
              >
                <Camera className="w-5 h-5 text-white" />
              </button>
            </div>
          )}

          {step !== 'scan_station' && step !== 'success' && (
            <button
              onClick={handleReset}
              className="p-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors flex-shrink-0"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </header>
      )}

      <div className={`flex-1 flex flex-col ${embedded ? 'p-0' : 'p-4 md:p-6 lg:p-8'} md:max-w-3xl lg:max-w-4xl md:mx-auto md:w-full`}>
        {/* Error Display */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
            <p className="text-red-700 dark:text-red-300 text-center">{error}</p>
          </div>
        )}

        {/* Step: Scan Station */}
        {step === 'scan_station' && (
          <div data-tour="clock-in-area" className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="w-24 h-24 md:w-32 md:h-32 mb-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <QrCode className="w-12 h-12 md:w-16 md:h-16 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('mobile.scanStationBarcode') || 'Scan Station Barcode'}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-8 md:text-base">
              {t('mobile.scanStationHint') || 'Point scanner at station barcode'}
            </p>

            {isLoading && (
              <div className="flex items-center gap-2 text-blue-600 mb-6">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processing...</span>
              </div>
            )}

            {/* Manual Entry */}
            <div className="w-full max-w-sm md:max-w-md lg:max-w-lg">
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 mb-2">
                {t('mobile.orEnterManually') || 'Or enter manually:'}
              </p>
              <div className="flex flex-col gap-3">
                <input
                  ref={stationInputRef}
                  type="text"
                  inputMode="none"
                  onTouchStart={(e) => (e.currentTarget.inputMode = 'text')}
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
                  placeholder="#STN-001"
                  className="w-full px-4 py-3 text-lg md:text-xl border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleManualSubmit}
                    disabled={!manualInput || isLoading}
                    className="flex-1 px-6 py-3 md:text-lg md:py-5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.go') || 'Go'}
                  </button>
                  <button
                    onClick={() => setShowCamera(true)}
                    className="px-4 py-3 md:py-5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 active:bg-gray-400 transition-colors"
                    title={t('cameraScanner.openCamera') || 'Use Camera'}
                  >
                    <Camera className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step: Scan Badge */}
        {step === 'scan_badge' && session && (
          <div data-tour="scan-badge-area" className="flex-1 flex flex-col">
            <ScanBadgeStep
              session={session}
              assignments={displayAssignments}
              embedded={embedded}
              manualInput={manualInput}
              onManualInputChange={setManualInput}
              onManualSubmit={handleManualSubmit}
              onShowCamera={() => setShowCamera(true)}
              onReset={handleReset}
              isLoading={isLoading}
              badgeInputRef={badgeInputRef}
            />
          </div>
        )}

        {/* Step: Supervisor Choice (Override vs Clock-in) */}
        {step === 'supervisor_choice' && employee && supervisorInfo && (
          <div className="flex-1 flex flex-col">
            {/* Employee Info */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 mb-6 shadow-sm text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 md:w-10 md:h-10 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{employee.display_name}</h2>
              <p className="text-gray-500">#{employee.employee_number}</p>
              <span className="inline-block mt-2 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium">
                {t('mobile.supervisor') || 'Supervisor'}
              </span>
            </div>

            {/* Choice Selection */}
            <h3 className="text-center text-gray-700 dark:text-gray-300 font-medium mb-4">
              {t('mobile.whatWouldYouLikeToDo') || 'What would you like to do?'}
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {/* Supervisor Override Option */}
              <button
                onClick={handleSupervisorOverride}
                disabled={isLoading}
                className="flex flex-col items-center justify-center p-6 md:p-8 rounded-2xl border-2 border-purple-300 bg-purple-50 dark:border-purple-700 dark:bg-purple-900/20 transition-all active:scale-95"
              >
                <span className="text-5xl md:text-6xl mb-3">🔑</span>
                <span className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                  {t('mobile.supervisorOverride') || 'Supervisor Override'}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t('mobile.labelReprintsManualWeight') || 'Label reprints, manual weight, etc.'}
                </span>
              </button>

              {/* Clock In as Operator or Packer - side by side */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <button
                  onClick={() => handleRoleSelect('operator', 'supervisor_choice')}
                  disabled={isLoading}
                  className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all active:scale-95 ${
                    displayAssignments.operator
                      ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
                      : 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20'
                  }`}
                >
                  <span className="text-4xl md:text-5xl mb-2">🔧</span>
                  <span className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">{t('mobile.operator') || 'Operator'}</span>
                  {displayAssignments.operator && (
                    <span className="text-xs text-amber-600 dark:text-amber-400 mt-1 text-center">
                      {displayAssignments.operator.employee_number}{displayAssignments.operator.name ? ` - ${displayAssignments.operator.name}` : ''}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleRoleSelect('packer', 'supervisor_choice')}
                  disabled={isLoading}
                  className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all active:scale-95 ${
                    displayAssignments.packer
                      ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
                      : 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                  }`}
                >
                  <span className="text-4xl md:text-5xl mb-2">📦</span>
                  <span className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">{t('mobile.packer') || 'Packer'}</span>
                  {displayAssignments.packer && (
                    <span className="text-xs text-amber-600 dark:text-amber-400 mt-1 text-center">
                      {displayAssignments.packer.employee_number}{displayAssignments.packer.name ? ` - ${displayAssignments.packer.name}` : ''}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {isLoading && (
              <div className="mt-4 flex items-center justify-center gap-2 text-purple-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t('common.loading') || 'Loading...'}</span>
              </div>
            )}
          </div>
        )}

        {/* Step: Auto Packer - loading while auto-clocking in */}
        {step === 'auto_packer' && employee && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 md:w-24 md:h-24 mx-auto mb-4 md:mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 md:w-12 md:h-12 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">{employee.display_name}</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-4 md:text-lg">
              {t('clockIn.clockingInAsPacker') || 'Clocking in as Packer...'}
            </p>
            <Loader2 className="w-6 h-6 md:w-8 md:h-8 animate-spin text-green-600" />
          </div>
        )}

        {/* Step: Confirm Relief */}
        {step === 'confirm_relief' && selectedRole && assignments[selectedRole] && (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
            <div className="w-20 h-20 md:w-28 md:h-28 mb-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <RefreshCw className="w-10 h-10 md:w-14 md:h-14 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('mobile.relieveWorker') || 'Relieve Worker?'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              <span className="font-semibold">{assignments[selectedRole]?.employee_number}{assignments[selectedRole]?.name ? ` - ${assignments[selectedRole]?.name}` : ''}</span>
            </p>
            <p className="text-gray-500 dark:text-gray-500 mb-8">
              {t('mobile.currentlyWorkingAs') || 'is currently working as'} <span className="capitalize font-medium">{selectedRole}</span>
            </p>

            <div className="w-full max-w-sm md:max-w-md space-y-3">
              <button
                onClick={handleConfirmRelief}
                disabled={isLoading}
                className="w-full py-4 md:py-6 bg-amber-600 text-white rounded-xl font-semibold text-lg md:text-xl hover:bg-amber-700 active:bg-amber-800 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    {t('mobile.yesRelieve') || 'Yes, Relieve Them'}
                  </>
                )}
              </button>
              <button
                onClick={() => setStep(roleSelectionSource === 'supervisor_choice' ? 'supervisor_choice' : 'scan_badge')}
                disabled={isLoading}
                className="w-full py-4 md:py-6 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-lg md:text-xl"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div data-tour="scan-success-area" className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="w-24 h-24 md:w-32 md:h-32 mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 md:w-16 md:h-16 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('mobile.clockedIn') || 'Clocked In!'}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 capitalize">
              {selectedRole}
            </p>
            {!isTourMode && (
              <div className="mt-6">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-green-600" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Slide-out Side Menu — hidden when embedded in Layout (Layout has its own sidebar) */}
      {!embedded && showSideMenu && (
        <MobileClockInSidebar
          isSupervisor={isSupervisor}
          onClose={() => setShowSideMenu(false)}
        />
      )}

      {/* Camera Scanner Modal — backup for USB barcode scanner */}
      <CameraScannerModal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onScan={(barcode) => {
          setShowCamera(false);
          handleScan(barcode);
        }}
      />

      {/* Supervisor Override Modal */}
      {fullSessionData && supervisorInfo && (
        <SupervisorOverrideModal
          isOpen={showSupervisorModal}
          onClose={() => {
            setShowSupervisorModal(false);
            setSupervisorInfo(null);
            setFullSessionData(null);
          }}
          supervisorId={supervisorInfo.id}
          supervisorName={supervisorInfo.name}
          supervisorEmployeeNumber={supervisorInfo.employee_number}
          session={fullSessionData}
          operatorEmployeeNumber={displayAssignments.operator?.employee_number}
          operatorName={displayAssignments.operator?.name}
          onManualWeightToggle={() => {
            // Manual weight toggle - not directly used in clock-in flow
          }}
        />
      )}
    </div>
  );
};

export default MobileClockIn;
